// Pantalla principal: "¿qué hago ahora?" (SKILL.md sección 4).
//
// Los vencimientos se agrupan por proyecto, una tarjeta plegable por cada uno.
// Antes venían agrupados sólo por fecha y las tareas de todos los proyectos se
// mezclaban en una lista larga: se abría la app y había que bajar tres
// pantallas. El agrupado en sí vive en src/lib/agrupar.ts, con tests.
//
// Todas las tarjetas arrancan plegadas: lo que se quiere ver al abrir la app
// es el mapa completo, y el encabezado de cada una ya dice lo que tiene
// adentro sin desplegarla.
//
// Lo demás (bloqueantes, presupuestos, proyectos) sigue llegando armado desde
// /api/agenda; acá sólo se pinta.

import { api } from "../api";
import { esc } from "../html";
import { iconoTilde, iconoBloqueada, iconoFlechaDer } from "../iconos";
import {
  atributoColor,
  ETIQUETA_TIPO_TAREA,
  ETIQUETA_PERIODICIDAD,
  claseTipoTarea,
  clasePrioridad,
  claseUrgencia,
  textoUrgencia,
} from "../etiquetas";
import type {
  Agenda,
  Bloqueante,
  ItemAgenda,
  PresupuestoAbierto,
  ProyectoListado,
  TipoTarea,
  Periodicidad,
} from "../../lib/tipos";
import { formatoLocal, urgencia } from "../../lib/fecha";
import { formatearMonto } from "../../lib/calculo";
import {
  agruparPorProyecto,
  calientes,
  totalesAgenda,
  type GrupoAgenda,
} from "../../lib/agrupar";

const plural = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`;

/** La misma clave que usa el mapa de plegados. */
const claveGrupo = (g: GrupoAgenda) =>
  g.proyecto_id === null ? "sin" : String(g.proyecto_id);

function chipDetalle(item: ItemAgenda): string {
  if (item.origen === "TAREA") {
    const tipo = item.detalle as TipoTarea;
    return `<span class="${claseTipoTarea(tipo)}">${ETIQUETA_TIPO_TAREA[tipo]}</span>`;
  }
  const p = item.detalle as Periodicidad;
  return `<span class="chip">${ETIQUETA_PERIODICIDAD[p]}</span>`;
}

/**
 * El resumen del encabezado del grupo: cuánto tiene adentro sin abrirlo.
 *
 * Vencida y "vence hoy" van separadas a propósito: decirle vencida a algo que
 * todavía está a tiempo es el tipo de aviso que uno aprende a ignorar.
 */
function chipsGrupo(g: GrupoAgenda): string {
  const cs: string[] = [];
  if (g.vencidas)
    cs.push(`<span class="chip chip-alerta">${plural(g.vencidas, "vencida", "vencidas")}</span>`);
  if (g.hoy) cs.push(`<span class="chip chip-alerta">${g.hoy} para hoy</span>`);
  if (g.semana) cs.push(`<span class="chip chip-acento">${g.semana} esta semana</span>`);
  if (g.lejos) cs.push(`<span class="chip">${g.lejos} más adelante</span>`);
  if (!cs.length) cs.push(`<span class="chip chip-ok">al día</span>`);
  return cs.join("");
}

/** Una tarea dentro de su grupo. Sin el nombre del proyecto: ya está arriba. */
function filaItem(item: ItemAgenda, hoy: string): string {
  const u = urgencia(item.fecha, hoy);
  const accion = item.origen === "TAREA" ? "Marcar hecha" : "Marcar cumplido";
  return `
    <div class="grupo-fila">
      <button type="button" class="tilde" data-origen="${item.origen}" data-id="${item.id}"
              aria-label="${accion}" title="${accion}">${iconoTilde}</button>
      <div class="min-w-0 flex-1 pt-2.5">
        <div class="break-words">${esc(item.titulo)}</div>
        <div class="mt-1.5 flex flex-wrap items-center gap-1.5">
          ${u ? `<span class="${claseUrgencia(u)}">${textoUrgencia(u, formatoLocal(item.fecha))}</span>` : ""}
          ${chipDetalle(item)}
          ${item.origen === "TAREA" ? `<span class="${clasePrioridad(item.prioridad)}">P${item.prioridad}</span>` : ""}
        </div>
      </div>
    </div>`;
}

/**
 * La tarjeta de un proyecto. Es un <details>: plegar y desplegar lo hace el
 * navegador, sin repintar la pantalla ni perder el scroll.
 *
 * `abierto` sale sólo de lo que Fernando desplegó a mano en esta visita: de
 * fábrica están todas cerradas.
 */
function tarjetaGrupo(g: GrupoAgenda, hoy: string, abierto: boolean): string {
  return `
    <details class="grupo color-marca ${calientes(g) ? "grupo-caliente" : ""}"
             data-grupo="${claveGrupo(g)}"${atributoColor(g.color)} ${abierto ? "open" : ""}>
      <summary class="grupo-cab">
        <span class="min-w-0 flex-1">
          <span class="block truncate font-semibold">${esc(g.nombre)}</span>
          <span class="mt-1.5 flex flex-wrap gap-1.5">${chipsGrupo(g)}</span>
        </span>
        <span class="chevron-grupo shrink-0 text-apagado">${iconoFlechaDer}</span>
      </summary>
      <div>
        ${g.items.map((i) => filaItem(i, hoy)).join("")}
        ${
          g.proyecto_id !== null
            ? `<a href="#/proyecto/${g.proyecto_id}"
                  class="grupo-fila items-center text-sm text-apagado transition hover:text-texto">
                 <span class="flex-1">Ir a ${esc(g.nombre)}</span>${iconoFlechaDer}
               </a>`
            : ""
        }
      </div>
    </details>`;
}

/** La frase de arriba de todo. Es lo primero que se lee al abrir la app. */
function resumen(t: { vencidas: number; hoy: number; proyectos: number }): string {
  if (!t.vencidas && !t.hoy) return "Nada vencido ni para hoy. Lo que queda puede esperar.";
  const donde = ` en ${plural(t.proyectos, "proyecto", "proyectos")}.`;
  if (!t.hoy) return plural(t.vencidas, "cosa vencida", "cosas vencidas") + donde;
  if (!t.vencidas) return plural(t.hoy, "cosa para hoy", "cosas para hoy") + donde;
  return `${plural(t.vencidas, "vencida", "vencidas")} y ${t.hoy} para hoy${donde}`;
}

function bloqueBloqueantes(bloqueantes: Bloqueante[]): string {
  if (!bloqueantes.length) return "";
  return `
    <section class="mt-6">
      <h2 class="mb-2 text-sm font-semibold uppercase tracking-wider text-apagado">
        Bloqueando a otras <span class="font-normal">(${bloqueantes.length})</span>
      </h2>
      <div class="space-y-2">
        ${bloqueantes
          .map(
            (b) => `
          <a href="#/proyecto/${b.proyecto_id}" class="fila flex items-start gap-3 p-3">
            <span class="mt-0.5 text-alerta">${iconoBloqueada}</span>
            <div class="min-w-0 flex-1">
              <div class="break-words">${esc(b.titulo)}</div>
              <div class="mt-1.5 flex flex-wrap items-center gap-1.5">
                <span class="chip chip-alerta">frena ${plural(b.frena_a, "tarea", "tareas")}</span>
                <span class="${claseTipoTarea(b.tipo)}">${ETIQUETA_TIPO_TAREA[b.tipo]}</span>
                <span class="text-xs text-apagado">${esc(b.proyecto_nombre)}</span>
              </div>
            </div>
          </a>`,
          )
          .join("")}
      </div>
    </section>`;
}

function bloquePresupuestos(presupuestos: PresupuestoAbierto[]): string {
  if (!presupuestos.length) return "";
  return `
    <section class="mt-6">
      <h2 class="mb-2 text-sm font-semibold uppercase tracking-wider text-apagado">
        Enviados sin respuesta <span class="font-normal">(${presupuestos.length})</span>
      </h2>
      <div class="space-y-2">
        ${presupuestos
          .map(
            (p) => `
          <a href="#/presupuesto/${p.id}" class="fila flex items-center gap-3 p-3">
            <span class="font-mono text-xs text-apagado">Nº ${p.numero}</span>
            <div class="min-w-0 flex-1">
              <div class="truncate">${esc(p.cliente_nombre)}</div>
              <div class="mt-0.5 text-xs text-apagado">${formatearMonto(p.total, p.moneda)}</div>
            </div>
            <span class="${p.dias_esperando >= 7 ? "chip chip-alerta" : "chip"}">
              hace ${p.dias_esperando} ${p.dias_esperando === 1 ? "día" : "días"}
            </span>
          </a>`,
          )
          .join("")}
      </div>
    </section>`;
}

function bloqueProyectos(proyectos: ProyectoListado[]): string {
  if (!proyectos.length) return "";
  return `
    <section class="mt-6">
      <h2 class="mb-2 text-sm font-semibold uppercase tracking-wider text-apagado">Proyectos activos</h2>
      <div class="space-y-2">
        ${proyectos
          .map(
            (p) => `
          <a href="#/proyecto/${p.id}"${atributoColor(p.color)}
             class="fila color-marca flex items-center gap-3 p-3 ${
               p.estado === "PAUSADO" ? "opacity-60" : ""
             }">
            <span class="${clasePrioridad(p.prioridad)}">P${p.prioridad}</span>
            <span class="min-w-0 flex-1 truncate">${esc(p.nombre)}</span>
            <span class="shrink-0 text-xs text-apagado">${
              p.pendientes ? plural(p.pendientes, "pendiente", "pendientes") : "al día"
            }</span>
          </a>`,
          )
          .join("")}
      </div>
    </section>`;
}

export async function renderAgenda(contenedor: HTMLElement): Promise<void> {
  // Qué tarjetas desplegó Fernando a mano. Sobrevive a los repintados de
  // marcar una tarea hecha; se pierde al salir de la pantalla, que es lo que
  // uno espera: se vuelve a entrar y están todas cerradas otra vez.
  const abiertos = new Map<string, boolean>();

  async function pintar(): Promise<void> {
    const a = await api.get<Agenda>("/api/agenda");

    // Los tres bloques por fecha se vuelven a juntar para reagruparlos por
    // proyecto. La API sigue igual: no hizo falta tocar el backend.
    const grupos = agruparPorProyecto(
      [...a.vencido_hoy, ...a.semana, ...a.avisados],
      a.hoy,
    );
    const totales = totalesAgenda(grupos);

    contenedor.innerHTML = `
      <h1 class="titulo-pagina">Qué hago ahora</h1>
      <p class="subtitulo">${formatoLocal(a.hoy)}</p>

      ${
        grupos.length
          ? `<p class="mt-3.5 text-pizarra-claro">${resumen(totales)}</p>`
          : ""
      }

      ${
        a.bandeja
          ? `<a href="#/bandeja" class="fila mt-3.5 flex items-center gap-3 p-3">
               <span class="chip chip-acento">${a.bandeja}</span>
               <span class="flex-1">${
                 a.bandeja === 1 ? "nota sin archivar" : "notas sin archivar"
               } en la bandeja</span>
               <span class="text-apagado">${iconoFlechaDer}</span>
             </a>`
          : ""
      }

      ${
        grupos.length
          ? `<div class="mt-4 space-y-2.5">
               ${grupos
                 .map((g) => tarjetaGrupo(g, a.hoy, abiertos.get(claveGrupo(g)) ?? false))
                 .join("")}
             </div>`
          : `<div class="tarjeta mt-5 text-center text-apagado">
               Nada vencido ni por vencer. ${
                 a.bloqueantes.length ? "Pero hay cosas frenando trabajo, mirá abajo." : "Respirá."
               }
             </div>`
      }

      ${bloqueBloqueantes(a.bloqueantes)}
      ${bloquePresupuestos(a.presupuestos)}
      ${bloqueProyectos(a.proyectos)}`;

    // Que el plegado sobreviva al repintado de marcar una tarea hecha.
    contenedor.querySelectorAll<HTMLDetailsElement>("details[data-grupo]").forEach((d) => {
      d.addEventListener("toggle", () => {
        if (d.dataset.grupo) abiertos.set(d.dataset.grupo, d.open);
      });
    });

    // Un toque para sacarse algo de encima, sin entrar al proyecto.
    contenedor.querySelectorAll<HTMLButtonElement>(".tilde").forEach((b) => {
      b.addEventListener("click", async () => {
        b.disabled = true;
        try {
          if (b.dataset.origen === "TAREA") {
            await api.put(`/api/tareas/${b.dataset.id}`, { estado: "HECHA" });
          } else {
            await api.post(`/api/recordatorios/${b.dataset.id}/cumplir`);
          }
          await pintar();
        } catch {
          b.disabled = false;
        }
      });
    });
  }

  try {
    await pintar();
  } catch (e) {
    contenedor.innerHTML = `<div class="tarjeta text-alerta">No se pudo cargar la agenda: ${esc(
      e instanceof Error ? e.message : "error desconocido",
    )}</div>`;
  }
}
